import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useCookies } from 'react-cookie';
import { IntlProvider } from 'react-intl';

import {
	getPreferredLocale,
	DEFAULT_LANGUAGE,
	LEFT_TO_RIGHT,
	RIGHT_TO_LEFT,
	RIGHT_TO_LEFT_LANGUAGES,
	TRANSLATED_LANGUAGES,
} from '../../utils/languages';

import { LanguageProvider } from './context';
import { polyfillLocaleData } from './utils/polyfill';

const propTypes = {

	/**
	 * The current user.
	 */
	currentUser: PropTypes.shape({

		/**
		 * The user's language preference.
		 */
		language: PropTypes.string,

	}),

	/**
	 * The current organization.
	 */
	myOrganization: PropTypes.shape({

		/**
		 * The default language for the organization.
		 */
		language: PropTypes.string.isRequired,

	}),

	/**
	 * The product for the current scope (if any).
	 */
	product: PropTypes.shape({

		/**
		 * The default language for the product.
		 */
		default_language: PropTypes.string.isRequired,

	}),

	/**
	 * The preloaded data required for SSR to render the LanguageWrapper component.
	 */
	preloadedData: PropTypes.shape({

		/**
		 * The preferred language for the user.
		 */
		preferredLanguage: PropTypes.string.isRequired,

		/**
		 * The translated message files to import for use with the react-intl library.
		 */
		messages: PropTypes.shape({}),

	}),

	/**
	 * Callback function to choose the correct translated message files
	 * to import in order to use with the react-intl library.
	 */
	selectAppLanguageFiles: PropTypes.func.isRequired,

	/**
	 * The component children.
	 */
	children: PropTypes.node.isRequired,

};

/**
 * The wrapper for the language context provider,
 * used to boot the context for the application as a whole.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object} result  The rendered component.
 */
const LanguageWrapper = (props) => {

	const {
		currentUser,
		myOrganization,
		product,
		preloadedData,
		selectAppLanguageFiles,
		children,
	} = props;

	const [cookies] = useCookies(['language']);

	// Get the preferred language for the user
	const preferredLanguage = useMemo(
		() => getPreferredLocale(currentUser, myOrganization, product, cookies),
		[currentUser, myOrganization, product, cookies],
	);

	// Keep track of the translated messages for the application
	const [languageMessages, setLanguageMessages] = useState(
		preloadedData?.preferredLanguage === preferredLanguage ? preloadedData?.messages : null
	);

	// Get the default language to use for the application
	const defaultLanguage =
		product?.default_language ||
		myOrganization?.language ||
		DEFAULT_LANGUAGE;

	// Choose the layout direction of content given the specified language
	const languageDirection =
		RIGHT_TO_LEFT_LANGUAGES[preferredLanguage] ? RIGHT_TO_LEFT : LEFT_TO_RIGHT;

	// Globally update 3rd-party libraries with the chosen language as it changes
	useEffect(() => {

		polyfillLocaleData(preferredLanguage);

	}, [preferredLanguage]);

	// Choose the correct set of translated messages to use for the application
	useEffect(() => {

		// Use the preloaded messages (if any) for the preferred language
		if (preferredLanguage === preloadedData?.preferredLanguage && preloadedData?.messages) {

			setLanguageMessages(preloadedData?.messages);

		// Otherwise, lazy load the messages for the preferred language
		} else {

			const importLanguage =
				TRANSLATED_LANGUAGES.indexOf(preferredLanguage) >= 0
					? preferredLanguage : DEFAULT_LANGUAGE;

			selectAppLanguageFiles(importLanguage).then((rMessages) => setLanguageMessages(rMessages));

		}

	}, [
		preloadedData?.preferredLanguage,
		preloadedData?.messages,
		preferredLanguage,
		selectAppLanguageFiles,
	]);

	return (
		<IntlProvider
			locale={preferredLanguage}
			messages={languageMessages}>
			<LanguageProvider
				preferredLanguage={preferredLanguage}
				defaultLanguage={defaultLanguage}>
				<div dir={languageDirection}>
					{children}
				</div>
			</LanguageProvider>
		</IntlProvider>
	);

};

LanguageWrapper.propTypes = propTypes;

export { LanguageWrapper };
